import { useCallback, useEffect, useMemo } from 'react';

import { useLocation } from 'react-router-dom';
import { create } from 'zustand';

import { Uuid, RfiDocumentResponse } from '@liscio/api';
import { useNavigation } from '@liscio/common';

import { WorkflowRPARequestResponse } from '../components/SmartTaxUpload/SmartTaxUpload';

// ==============================
// Type Definitions
// ==============================

// Named constants for tab values - use these instead of string literals
export const SMART_TAX_TAB = {
  IMPORT: 'import',
  PROCESSING: 'processing',
  ORGANIZER_REQUESTS: 'organizer-requests',
  ORGANIZER_TEMPLATES: 'organizer-templates',
  DELIVERY_REQUESTS: 'delivery-requests',
  PENDING_APPROVAL: 'pending-approval',
} as const;

// Array of valid tabs for iteration/validation
export const SMART_TAX_TABS = Object.values(SMART_TAX_TAB);

// Derive the type from the const object
export type SmartTaxTab = (typeof SMART_TAX_TAB)[keyof typeof SMART_TAX_TAB];

// Type guard for validating tab values
export const isValidSmartTaxTab = (tab: string): tab is SmartTaxTab =>
  SMART_TAX_TABS.includes(tab as SmartTaxTab);

type ImportPageState = null | 'organizer' | 'delivery';

// Union type for processing list to support both RPA and RFI workflows
type ProcessingListItem = PromiseSettledResult<WorkflowRPARequestResponse | RfiDocumentResponse>;

// Define the possible events our FSM can handle
type SmartTaxEvent =
  | { type: 'CHANGE_TAB'; tab: SmartTaxTab }
  | { type: 'GO_TO_IMPORT_LANDING' }
  | { type: 'GO_TO_ORGANIZER' }
  | { type: 'GO_TO_DELIVERY' }
  | { type: 'SET_OWNER_ID'; ownerId: Uuid | null }
  | { type: 'SET_IS_UPLOADING'; isUploading: boolean }
  | {
      type: 'SET_PROCESSING_LIST';
      processingList: ProcessingListItem[];
    };

// Define the state shape for our store
type SmartTaxState = {
  currentTab: SmartTaxTab;
  importPage: ImportPageState;
  ownerId: Uuid | null;
  isUploading: boolean;
  dispatch: (event: SmartTaxEvent) => void;
  processingList: ProcessingListItem[];
};

// Define the location state type
type SmartTaxLocationState = {
  smartTax?: {
    importPage: ImportPageState;
  };
};

// ==============================
// Store Definition
// ==============================

export const useSmartTaxStore = create<SmartTaxState>((set) => ({
  currentTab: 'import',
  importPage: null,
  ownerId: null,
  isUploading: false,
  processingList: [] as ProcessingListItem[],
  dispatch: (event: SmartTaxEvent) => {
    switch (event.type) {
      case 'CHANGE_TAB':
        set({
          currentTab: event.tab,
          // Reset import page when changing tabs
          importPage: null,
        });
        break;

      case 'GO_TO_IMPORT_LANDING':
        set({
          currentTab: 'import',
          importPage: null,
        });
        break;

      case 'GO_TO_ORGANIZER':
        set({
          currentTab: 'import',
          importPage: 'organizer',
        });
        break;

      case 'GO_TO_DELIVERY':
        set({
          currentTab: 'import',
          importPage: 'delivery',
        });
        break;

      case 'SET_OWNER_ID':
        set({
          ownerId: event.ownerId,
        });
        break;

      case 'SET_IS_UPLOADING':
        set({
          isUploading: event.isUploading,
        });
        break;

      case 'SET_PROCESSING_LIST':
        set({
          processingList: event.processingList,
        });
        break;

      default:
        break;
    }
  },
}));

// ==============================
// Selectors
// ==============================

export const useSmartTaxTab = () =>
  useSmartTaxStore((state) => state.currentTab);

export const useImportPage = () =>
  useSmartTaxStore((state) => state.importPage);

export const useSmartTaxOwnerId = () =>
  useSmartTaxStore((state) => state.ownerId);

export const useSmartTaxIsUploading = () =>
  useSmartTaxStore((state) => state.isUploading);

// ==============================
// Action Creators
// ==============================

export const useSmartTaxActions = () => {
  const dispatch = useSmartTaxStore((state) => state.dispatch);
  const { navigateTo } = useNavigation();
  const location = useLocation();
  const currentPath = location.pathname;

  const setProcessingList = useCallback(
    (processingList: ProcessingListItem[]) => {
      dispatch({ type: 'SET_PROCESSING_LIST', processingList });
    },
    [dispatch]
  );

  return useMemo(
    () => ({
      changeTab: (tab: SmartTaxTab) => dispatch({ type: 'CHANGE_TAB', tab }),

      goToImportLanding: () => dispatch({ type: 'GO_TO_IMPORT_LANDING' }),

      goToOrganizer: () => {
        dispatch({ type: 'GO_TO_ORGANIZER' });
        navigateTo(currentPath, {
          state: { smartTax: { importPage: 'organizer' } },
        });
      },

      goToDelivery: () => {
        dispatch({ type: 'GO_TO_DELIVERY' });
        navigateTo(currentPath, {
          state: { smartTax: { importPage: 'delivery' } },
        });
      },

      setOwnerId: (ownerId: Uuid | null) =>
        dispatch({ type: 'SET_OWNER_ID', ownerId }),

      setIsUploading: (isUploading: boolean) =>
        dispatch({ type: 'SET_IS_UPLOADING', isUploading }),

      setProcessingList,
    }),
    [dispatch, navigateTo, currentPath, setProcessingList]
  );
};

// ==============================
// Navigation Helper
// ==============================

export const useSmartTaxBackNavigation = () => {
  const { goToImportLanding } = useSmartTaxActions();
  const location = useLocation();

  useEffect(() => {
    // Check location state when it changes
    const state = location.state as SmartTaxLocationState | undefined;
    const smartTaxState = state?.smartTax;

    // When user navigates back (state will be null or won't have our SmartTax data),
    // reset to the landing page
    if (!smartTaxState || !smartTaxState.importPage) {
      goToImportLanding();
    }
  }, [location, goToImportLanding]);
};
